import { z } from 'zod';
import { MediaTypeEnum } from './media-unit-variants.js';
import {
  BulkPhotoImportResultRowSchema,
  BulkPhotoImportResultSummarySchema,
} from './bulk-photo-import.js';
import { TransformDescriptorSchema, ColumnTransformSuggestionSchema } from './import-transform.js';

// ── Transform descriptor (moved to its own file to keep this one small) ──────
export {
  TransformDescriptorSchema,
  PatternTransformSchema,
  ConstantTransformSchema,
  ValueMapTransformSchema,
  ColumnTransformSuggestionSchema,
  PATTERN_MAX_LENGTH,
  TRANSFORM_VALUE_MAX_LENGTH,
  VALUE_MAP_MAX_ENTRIES,
} from './import-transform.js';
export type {
  TransformDescriptor,
  PatternTransform,
  ConstantTransform,
  ValueMapTransform,
  ColumnTransformSuggestion,
} from './import-transform.js';

// ── Column mapping types (moved from mockups) ─────────────────────────────────

export const FieldOptionItemSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  group: z.enum(['general', 'physical', 'mural_flags', 'digital', 'internal_cost', 'client_price']),
  required: z.boolean().optional(),
});
export type FieldOptionItem = z.infer<typeof FieldOptionItemSchema>;

export const ColumnMappingItemSchema = z.object({
  id: z.string(),
  sourceColumn: z.string(),
  mappedTo: z.string().nullable(),
  /** Optional pattern/constant/valueMap transform applied when producing the mapped row. */
  transform: TransformDescriptorSchema.optional(),
});
export type ColumnMappingItem = z.infer<typeof ColumnMappingItemSchema>;

export const SupplierOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type SupplierOption = z.infer<typeof SupplierOptionSchema>;

// ── FIELD_OPTIONS and suggestColumnMapping (in sub-file to keep line count low) ──

export {
  FIELD_OPTIONS,
  suggestColumnMapping,
  FIELD_OPTION_KEY_TO_DB_COLUMN,
  TAG_DB_COLUMN,
  resolveMappedToColumn,
} from './import-job-field-options.js';

// ── New response/request schemas ──────────────────────────────────────────────

export const ImportColumnsResponseSchema = z.object({
  detectedColumns: z.array(z.string()),
  suggestedMapping: z.array(ColumnMappingItemSchema),
  suppliers: z.array(SupplierOptionSchema),
  savedMapping: z.array(ColumnMappingItemSchema).optional(),
  /** Adapter-driven column lists surfaced when mediaTypeOverride is set on the job. */
  adapterColumns: z
    .object({
      required: z.array(z.string()),
      optional: z.array(z.string()),
    })
    .optional(),
  /** Sheet names available in the uploaded workbook (multi-sheet files). */
  sheets: z.array(z.string()).optional(),
  /** 0-based index of the detected header row (see detectHeaderRow). */
  headerRowIndex: z.number().optional(),
  /** Advisory pattern-transform suggestions per detected column. */
  transformSuggestions: z.array(ColumnTransformSuggestionSchema).optional(),
  /** Non-empty sample values per detected column (header label -> up to SAMPLE_ROWS_LIMIT values). */
  columnSamples: z.record(z.string(), z.array(z.string())).optional(),
  /** Full distinct non-empty source values per detected column, for value-map editing. */
  columnDistinctValues: z.record(z.string(), z.array(z.string())).optional(),
});
export type ImportColumnsResponse = z.infer<typeof ImportColumnsResponseSchema>;

export const ConfirmMappingRequestSchema = z.object({
  mappings: z.array(ColumnMappingItemSchema),
  saveForSupplierId: z.string().optional(),
  /** Per-file adapter type override; pins the adapter for all rows in this import job. */
  mediaTypeOverride: MediaTypeEnum.optional(),
  /**
   * Selected workbook sheet for multi-sheet files. Persisted with the job (and the
   * signature-keyed remembered config) so the worker applies the same sheet later.
   */
  sheetName: z.string().optional(),
});
export type ConfirmMappingRequest = z.infer<typeof ConfirmMappingRequestSchema>;

export const ConfirmMappingResponseSchema = z.object({
  ok: z.literal(true),
  importJobId: z.number(),
});
export type ConfirmMappingResponse = z.infer<typeof ConfirmMappingResponseSchema>;

/**
 * Full confirmed import configuration persisted per column signature (see
 * `column-mapping-api/spec.md` "Remembered configuration keyed by column
 * signature"). `mapping` already carries per-column `transform` descriptors
 * (pattern/constant/valueMap), so this shape doesn't need separate
 * `transforms`/`valueMaps` fields — `sheetName` is the only addition.
 */
export const RememberedImportConfigSchema = z.object({
  mapping: z.array(ColumnMappingItemSchema),
  sheetName: z.string().optional(),
});
export type RememberedImportConfig = z.infer<typeof RememberedImportConfigSchema>;

// ── Supplier dedup schemas ─────────────────────────────────────────────────────

const SUPPLIER_RESOLUTION_MAX_ENTRIES = 500;
const SUPPLIER_NAME_MAX_LENGTH = 300;

/** Maps ambiguous supplier name from import file -> canonical supplier name (or null to skip). */
export const SupplierResolutionMapSchema = z
  .record(
    z.string().max(SUPPLIER_NAME_MAX_LENGTH),
    z.string().max(SUPPLIER_NAME_MAX_LENGTH).nullable(),
  )
  .refine((map) => Object.keys(map).length <= SUPPLIER_RESOLUTION_MAX_ENTRIES, {
    message: `Resolution map must contain at most ${SUPPLIER_RESOLUTION_MAX_ENTRIES} entries`,
  });
export type SupplierResolutionMap = z.infer<typeof SupplierResolutionMapSchema>;

export const SupplierSuggestionItemSchema = z.object({
  name: z.string(),
  score: z.number(),
});
export type SupplierSuggestionItem = z.infer<typeof SupplierSuggestionItemSchema>;

export const SuggestSuppliersResponseSchema = z.object({
  suggestions: z.array(SupplierSuggestionItemSchema),
});
export type SuggestSuppliersResponse = z.infer<typeof SuggestSuppliersResponseSchema>;

/** A single ambiguous supplier name from the import file, with ranked existing-supplier suggestions. */
export const AmbiguousSupplierEntrySchema = z.object({
  name: z.string(),
  suggestions: z.array(SupplierSuggestionItemSchema),
});
export type AmbiguousSupplierEntry = z.infer<typeof AmbiguousSupplierEntrySchema>;

export const SaveSupplierResolutionsResponseSchema = z.object({
  ok: z.literal(true),
  importJobId: z.number(),
});
export type SaveSupplierResolutionsResponse = z.infer<typeof SaveSupplierResolutionsResponseSchema>;

export const SuggestSuppliersQuerySchema = z.object({ q: z.string().trim().min(1).max(200) });
export type SuggestSuppliersQuery = z.infer<typeof SuggestSuppliersQuerySchema>;

/**
 * GET /api/import/template query params — `type` selects which template
 * workbook to stream. Defaults to 'ooh' when omitted (bulk-import-api spec,
 * "GET /api/import/template" requirement). An unrecognized value (e.g.
 * `?type=mpk`) is rejected with a structured 400 by the global
 * ZodValidationPipe before the controller/service ever runs.
 */
export const GetTemplateQuerySchema = z.object({
  type: z.enum(['ooh', 'dooh']).optional(),
});
export type GetTemplateQuery = z.infer<typeof GetTemplateQuerySchema>;

export const SaveSupplierResolutionsRequestSchema = z.object({
  resolutions: SupplierResolutionMapSchema,
});
export type SaveSupplierResolutionsRequest = z.infer<typeof SaveSupplierResolutionsRequestSchema>;

// ── Shared constants ──────────────────────────────────────────────────────────

/** Maximum allowed upload file size for import files (10 MB). */
export const IMPORT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Maximum number of data rows accepted by a single media-unit import (REQ-2.9).
 * Single source of truth for both the API-level upload guard (fails fast, before
 * the file ever reaches validation/mapping), the worker's defense-in-depth guard
 * in `MediaUnitImportJob` (CU-86caecmcr — files that slipped past an older API
 * build must still fail with a clear reason instead of silently completing with
 * all-zero counters), and the standard/DOOH template generators' data-validation
 * row range and "Limity" instruction text.
 *
 * Raised from 500 to 1000 (CU-86caecmcr follow-up) — 500 was an MVP-era estimate
 * of a typical supplier refresh, not a measured technical ceiling; a real client
 * file already exceeded it. No per-row external rate limit exists in the import
 * pipeline (location enrichment is a local PostGIS query, not a geocoding API
 * call) and BullMQ job locks auto-renew, so there's no hard timeout tied to this
 * number. The remaining real constraint is `MediaUnitImportJob`'s queue
 * (`concurrency: 1`) processing one import at a time — a very large file still
 * delays other admins' imports for its duration, which is why this stays a
 * bounded limit rather than being removed outright.
 */
export const IMPORT_MAX_DATA_ROWS = 1000;

// ── Column definitions (shared between template generator, API, and worker) ──

export interface ImportTemplateColumn {
  header: string;
  fieldKey: string;
  required: boolean;
}

/**
 * Canonical column configuration for the standard import template.
 * D-8: Single source of truth consumed by template generator, upload endpoint, and worker.
 * Aligned to Marta's 47-column NOSNIKI_HEADERS (szablon_OOH.xlsx).
 * Required headers are the 9 columns that must be present for the file to be accepted.
 */
export const IMPORT_TEMPLATE_COLUMNS: ImportTemplateColumn[] = [
  // ── 1. Location ───────────────────────────────────────────────
  { header: 'Województwo', fieldKey: 'voivodeship', required: false },
  { header: 'Powiat', fieldKey: 'county', required: false },
  { header: 'Aglomeracja', fieldKey: 'agglomeration', required: false }, // NOTE: BEFORE Miejscowość
  { header: 'Miejscowość', fieldKey: 'city', required: true },
  { header: 'Adres', fieldKey: 'address', required: true },
  { header: 'Nr drogi (dla konstrukcji)', fieldKey: 'road_number', required: false },
  { header: 'Współrzędne - szerokość', fieldKey: 'latitude', required: true },
  { header: 'Współrzędne - długość', fieldKey: 'longitude', required: true },
  // ── 2. Identification ─────────────────────────────────────────
  { header: 'Numer Grupy RW', fieldKey: 'grw_id', required: true },
  { header: 'Link do karty video dla WF', fieldKey: 'videocard_link', required: false },
  { header: 'Numer dostawcy', fieldKey: 'supplier_number', required: true },
  { header: 'Dostawca', fieldKey: 'supplier', required: true },
  { header: 'Typ nośnika', fieldKey: 'type', required: true },
  { header: 'Format', fieldKey: 'format', required: true },
  // ── 3. Physical params ────────────────────────────────────────
  { header: 'Szerokość [m]', fieldKey: 'width_m', required: false },
  { header: 'Wysokość [m]', fieldKey: 'height_m', required: false },
  { header: 'Wymiary niestandardowe', fieldKey: 'dimension_notes', required: false },
  { header: 'Materiał', fieldKey: 'material', required: false },
  { header: 'Oświetlenie', fieldKey: 'lighting', required: false },
  // ── 4. Audience ───────────────────────────────────────────────
  { header: 'Szacowana liczba odbiorców', fieldKey: 'audience_estimate', required: false },
  // ── 5. Multiformat ────────────────────────────────────────────
  { header: 'Multiformat', fieldKey: 'multiformat', required: false },
  // ── 6. Common metadata ────────────────────────────────────────
  {
    header: 'Osoba do kontaktu przy WF i muralach',
    fieldKey: 'wf_contact',
    required: false,
  },
  { header: 'Opis lokalizacji', fieldKey: 'location_description', required: false },
  { header: 'Uwagi', fieldKey: 'notes', required: false },
  // ── 7. Mural variant flags ────────────────────────────────────
  { header: '[Mural] Element 3D', fieldKey: 'mural_element_3d', required: false },
  { header: '[Mural] Taśma LED', fieldKey: 'mural_led_tape', required: false },
  { header: '[Mural] Neon LED', fieldKey: 'mural_neon_led', required: false },
  { header: '[Mural] Fluo UV', fieldKey: 'mural_fluo_uv', required: false },
  // ── 8. Costs / client prices ──────────────────────────────────
  { header: 'Czynsz', fieldKey: 'rent', required: false },
  { header: 'Koszt ekspozycji', fieldKey: 'exposure_cost', required: false },
  { header: 'Cena katalogowa', fieldKey: 'price_net', required: false },
  { header: 'Cena po rabacie', fieldKey: 'price_discounted', required: false },
  { header: 'Cena last minute', fieldKey: 'price_last_minute', required: false },
  { header: 'RW na start', fieldKey: 'starter_price', required: false },
  { header: 'Koszt produkcji', fieldKey: 'production_cost', required: false },
  { header: 'Produkcja', fieldKey: 'client_production', required: false },
  { header: 'Koszt montażu', fieldKey: 'install_cost', required: false },
  { header: 'Montaż', fieldKey: 'client_installation', required: false },
  { header: 'Koszt demontażu', fieldKey: 'dismantling_cost', required: false },
  { header: 'Demontaż', fieldKey: 'client_dismantling', required: false },
  { header: 'Koszty dodatkowe', fieldKey: 'additional_costs', required: false },
  { header: 'Opłaty dodatkowe', fieldKey: 'client_additional_fees', required: false },
  { header: '[Mural] Produkcja eco muralu', fieldKey: 'mural_eco_production', required: false },
  // ── 9. Tags ───────────────────────────────────────────────────
  { header: 'Tag 1', fieldKey: 'tag_1', required: false },
  { header: 'Tag 2', fieldKey: 'tag_2', required: false },
  { header: 'Tag 3', fieldKey: 'tag_3', required: false },
  { header: 'Tag 4', fieldKey: 'tag_4', required: false },
  { header: 'Tag 5', fieldKey: 'tag_5', required: false },
  // ── 10. Photos ────────────────────────────────────────────────
  { header: 'Zdjęcia', fieldKey: 'photo_urls', required: false },
];

/** The 9 required header strings (without the * suffix used in the XLSX file). */
export const REQUIRED_IMPORT_HEADERS = IMPORT_TEMPLATE_COLUMNS.filter((c) => c.required).map(
  (c) => c.header,
);

// ── Zod schemas ───────────────────────────────────────────────────────────────

export const ImportJobStatusSchema = z.enum([
  'pending',
  'mapping',
  'processing',
  'completed',
  'failed',
]);
export type ImportJobStatus = z.infer<typeof ImportJobStatusSchema>;

export const ImportJobSchema = z.object({
  id: z.number(),
  type: z.string(),
  status: ImportJobStatusSchema,
  // sourceFileKey intentionally excluded — R2 internal key must not be exposed to clients
  originalName: z.string().nullable(),
  totalRows: z.number().nullable(),
  processedRows: z.number().nullable(),
  updatedRows: z.number().nullable(),
  errorRows: z.number().nullable(),
  /** Per-photo result rows for bulk_photo_import jobs. Present after job completion. */
  errorLog: z.array(BulkPhotoImportResultRowSchema).optional(),
  /**
   * Aggregated photo-bundle outcome. Optional: absent for non-photo-bundle jobs and for
   * historical jobs that predate the result-summary change.
   */
  resultSummary: BulkPhotoImportResultSummarySchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
});
export type ImportJob = z.infer<typeof ImportJobSchema>;

export const ImportUploadResponseSchema = z.object({
  importJobId: z.number(),
  totalRows: z.number().nullable(),
  requiresMapping: z.boolean(),
  detectedColumns: z.array(z.string()).optional(),
  /** Supplier names from the import file that could not be uniquely matched. */
  ambiguousSuppliers: z.array(AmbiguousSupplierEntrySchema).optional().default([]),
});
export type ImportUploadResponse = z.infer<typeof ImportUploadResponseSchema>;

export const ImportStartResponseSchema = z.object({
  ok: z.literal(true),
  importJobId: z.number(),
});
export type ImportStartResponse = z.infer<typeof ImportStartResponseSchema>;

/**
 * Raw identifying columns for one availability-import row (supplier number,
 * GPS, address — whichever the profile declared), mirroring the shape
 * `extractRowIdentifiers` (`packages/db/src/queries/availability-windows/
 * interpret-row.ts`) already builds for unit matching. Duplicated here
 * (rather than imported) because `packages/contracts` MUST NOT depend on
 * `@ooh/db`. Attached to an unmatched row's errorLog entry so the
 * downloaded CSV can show which raw row a supplier needs to fix instead of
 * just an opaque row number.
 */
export const ImportErrorRowIdentifiersSchema = z.object({
  grwId: z.string().nullable(),
  supplierNumber: z.string().nullable(),
  supplierName: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
});
export type ImportErrorRowIdentifiers = z.infer<typeof ImportErrorRowIdentifiersSchema>;

export const ImportErrorRowSchema = z.object({
  row: z.number(),
  grwId: z.string().optional(),
  field: z.string(),
  message: z.string(),
  code: z.string().optional(),
  params: z.record(z.string(), z.string()).optional(),
  severity: z.enum(['error', 'warning', 'autofix', 'success']).optional(),
  /** Raw identifying columns for the row, present when the failure is an unmatched unit. */
  identifiers: ImportErrorRowIdentifiersSchema.optional(),
});
export type ImportErrorRow = z.infer<typeof ImportErrorRowSchema>;

export const ImportResultSchema = z.object({
  imported: z.number(),
  updated: z.number(),
  skipped: z.number(),
  errors: z.number(),
  totalRows: z.number(),
});
export type ImportResult = z.infer<typeof ImportResultSchema>;

export const ImportErrorsResponseSchema = z.object({
  importJobId: z.number(),
  errors: z.array(ImportErrorRowSchema),
});
export type ImportErrorsResponse = z.infer<typeof ImportErrorsResponseSchema>;

export const AutofixRowSchema = z.object({
  row: z.number(),
  grwId: z.string().optional(),
  field: z.string(),
  message: z.string(),
  code: z.string().optional(),
  params: z.record(z.string(), z.string()).optional(),
  severity: z.literal('autofix'),
  originalValue: z.string().optional(),
  fixedValue: z.string().optional(),
});
export type AutofixRow = z.infer<typeof AutofixRowSchema>;

export const ImportValidateResponseSchema = z.object({
  totalRows: z.number(),
  errorRows: z.number(),
  warningRows: z.number(),
  errors: z.array(ImportErrorRowSchema),
  autofixes: z.array(AutofixRowSchema),
});
export type ImportValidateResponse = z.infer<typeof ImportValidateResponseSchema>;
